import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

// Admin console screen 5 of 5 (pages/SettingsPage.jsx), route /settings.
// One global settings object, not a list — GET/PUT /admin/settings (see
// routes/adminSettingsRoutes.js). No id in the query key: there is exactly
// one settings row-set for the whole system.
const SETTINGS_KEY = ['admin', 'settings'];

// GET /admin/settings -> {settings: {...camelCase fields...}}. Field list
// is the exact and only one this screen renders (see
// services/settingsService.js's own comment on why signature/stamp storage
// paths and the cron-alert-timing knobs are deliberately excluded from the
// response entirely, not merely hidden client-side).
export function useAdminSettings() {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => apiClient.get('/admin/settings'),
    select: (data) => data.settings,
  });
}

// PUT /admin/settings — a PARTIAL object of only the fields the admin
// actually changed (routes/adminSettingsRoutes.js's zod schema treats every
// field as optional; pages/SettingsPage.jsx builds the diff before calling
// this). The response is already the complete, authoritative post-save
// shape (same {settings: {...}} envelope as the GET), so this uses
// setQueryData directly with the raw response rather than merge-then-
// invalidate — no second round trip needed, unlike useAdminUsers.js's
// create/deactivate mutations, which only get a single changed row back.
export function useUpdateAdminSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch) => apiClient.put('/admin/settings', patch),
    onSuccess: (data) => {
      queryClient.setQueryData(SETTINGS_KEY, data);
    },
  });
}

export default useAdminSettings;
